import { SFX_LIBRARY, sfxFile } from '@repo/editor-core';

/**
 * Preview-side SFX player. Fetches the real mp3 assets from `/sfx/<file>`,
 * decodes them to AudioBuffers, caches, and plays one-shots — the same files
 * the ECS export renderer mixes, so preview === export. Uses one shared
 * AudioContext for both the timeline scheduler and the picker preview.
 */
let ctx: AudioContext | null = null;
const cache = new Map<string, AudioBuffer>();
const inflight = new Map<string, Promise<AudioBuffer | null>>();
// Currently-sounding one-shots, so a pause/scrub can cut them (they'd otherwise
// ring on to their natural end — very audible now that sfx use full length).
const active = new Set<AudioBufferSourceNode>();

function getCtx(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  if (!ctx) ctx = new AudioContext();
  return ctx;
}

/** Resume the shared SFX AudioContext — call inside a user gesture (play/click). */
export function resumeSfx(): void {
  void getCtx()?.resume();
}

function load(code: string): Promise<AudioBuffer | null> {
  const cached = cache.get(code);
  if (cached) return Promise.resolve(cached);
  const existing = inflight.get(code);
  if (existing) return existing;
  const file = sfxFile(code);
  const c = getCtx();
  if (!file || !c) return Promise.resolve(null);
  const p = (async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}sfx/${file}`);
      if (!res.ok) return null;
      const decoded = await c.decodeAudioData(await res.arrayBuffer());
      cache.set(code, decoded);
      return decoded;
    } catch {
      return null;
    } finally {
      inflight.delete(code);
    }
  })();
  inflight.set(code, p);
  return p;
}

/** Preload (fetch+decode) sfx buffers so playback has zero first-hit latency. */
export async function preloadSfx(codes: Iterable<string>): Promise<void> {
  await Promise.all([...new Set(codes)].map((c) => load(c)));
}

function start(buffer: AudioBuffer, gainMul: number): void {
  const c = getCtx();
  if (!c) return;
  const gain = c.createGain();
  gain.gain.value = Math.max(0, gainMul);
  gain.connect(c.destination);
  const src = c.createBufferSource();
  src.buffer = buffer;
  src.connect(gain);
  src.onended = () => {
    active.delete(src);
    try {
      gain.disconnect();
    } catch {
      /* already disconnected */
    }
  };
  active.add(src);
  src.start();
}

/** Stop every sounding one-shot immediately (transport pause / scrub / dispose). */
export function stopSfx(): void {
  for (const src of active) {
    try {
      src.stop();
    } catch {
      /* already stopped */
    }
  }
  active.clear();
}

/**
 * Play a one-shot sfx by id. If the buffer isn't cached yet it's fetched+decoded
 * first (a small one-time delay); preload via preloadSfx for latency-free cues.
 */
export function playSfx(sfxId: string, gainMul = 1): void {
  if (!SFX_LIBRARY[sfxId]) return;
  const cached = cache.get(sfxId);
  if (cached) {
    start(cached, gainMul);
    return;
  }
  void load(sfxId).then((b) => b && start(b, gainMul));
}
